/**
 * Whether this is a wine with bubbles in it.
 *
 * The three sparkling types travel together everywhere — the registration form
 * shows the Champagne fields for exactly these, and so does the walk through
 * the cellar — so the set is named once rather than written out at each place
 * that asks. Crémant and the plain sparkling are in it because a back label is
 * a back label: a Crémant lies on its lees and is disgorged like anything else
 * made the same way.
 */
export function sparkles(type) {
    return type === "Champagne" || type === "Cremant" || type === "Sparkling";
}
/**
 * What is missing, in the order the fields are asked for.
 *
 * A vintage first, because a wine with no year and no flag saying it has none
 * is the one case where the record does not even say which kind of wine it is
 * — two of this cellar's eleven are named `N.V.` in the name column without
 * being marked as non-vintage, which is how the spreadsheet said it and not
 * something the database can read.
 *
 * Then the base vintage, and only for sparkling wine: a non-vintage still wine
 * is a blend of years nobody records, while a non-vintage Champagne has a base
 * year that its producer knows and often prints.
 *
 * Then disgorgement, for every sparkling wine whether or not it carries a
 * vintage. A 2008 disgorged in 2023 is a young wine in 2024, and that is true
 * of the vintage bottling exactly as much as of the NV.
 */
export function gapsInWhatIsKnown(wine) {
    const gaps = [];
    if (!wine.isNonVintage && wine.vintage === null)
        gaps.push("vintage");
    if (wine.isNonVintage && sparkles(wine.type) && wine.baseVintage === null) {
        gaps.push("baseVintage");
    }
    if (sparkles(wine.type) && wine.disgorgedOn === null)
        gaps.push("disgorgement");
    return gaps;
}
/**
 * The three that ask on their own, and therefore what the walk opens on.
 *
 * Named here rather than in the screen for the reason `defaultWineFilter` is
 * named in `wine-filter.ts`: a default written down twice is a default that
 * will eventually be two.
 */
export const ANCHORS = ["vintage", "baseVintage", "disgorgement"];
/**
 * All six, in the order they are offered and the order a row asks for them —
 * the anchors first because they are what the list is for, then the two that
 * are read off the back label, then the photograph of it.
 */
export const MISSING_FACTS = [
    "vintage",
    "baseVintage",
    "disgorgement",
    "dosage",
    "alcohol",
    "photo",
];
export const LABEL_FACTS = [
    "baseVintage",
    "disgorgement",
    "dosage",
    "alcohol",
];
export function isLabelFact(fact) {
    return LABEL_FACTS.includes(fact);
}
/**
 * Everything this wine is missing, in the order above.
 *
 * The dosage is asked only of sparkling wine, because that is what a dosage is
 * a fact about. The alcohol is asked of every wine, because every bottle
 * states one — the same sentence that kept it out of the Champagne fields on
 * the registration form. The photograph is asked of every wine that can be
 * photographed, which is every wine whose count arrived.
 *
 * `alcoholPercent` is read for undefined as well as null: its contract is
 * still lenient for the release it went out in, so an older API's response
 * parses to a field that is simply absent, and absent is exactly as missing as
 * null.
 */
export function whatIsMissing(wine) {
    const missing = [...gapsInWhatIsKnown(wine)];
    if (sparkles(wine.type) && wine.dosageGramsPerLitre === null)
        missing.push("dosage");
    if (wine.alcoholPercent === null || wine.alcoholPercent === undefined) {
        missing.push("alcohol");
    }
    if (wine.photoCount === 0)
        missing.push("photo");
    return missing;
}
/**
 * What is missing and still worth picking the bottle up for: the above, less
 * the facts somebody has read the label for and found not printed.
 *
 * Two functions rather than one, because they answer different questions and
 * both are asked. `whatIsMissing` is what the record does not know, and it
 * does not stop not knowing because a label was silent — the window
 * suggestion still has nothing to count from. This is what a walk past the
 * cabinets can still learn.
 */
export function stillToRead(wine) {
    const silent = new Set(wine.labelSilentOn ?? []);
    return whatIsMissing(wine).filter((fact) => !silent.has(fact));
}
/**
 * Whether this wine is summoned by a walk looking for these things.
 *
 * An *or* rather than an *and*: ticking two boxes asks for the wines that owe
 * either, because the errand is *fill in what is empty while I am down here*
 * and a wine missing only one of them is still a bottle to pick up. An empty
 * choice summons nobody, which is a state the screen lets exist and says so
 * rather than quietly falling back to showing everything.
 */
export function walkedFor(wine, looking) {
    // What can still be read, not what is missing: a label already found silent
    // on the fact being walked for is not a reason to pick the bottle up again.
    return stillToRead(wine).some((fact) => looking.has(fact));
}
//# sourceMappingURL=wine-gaps.js.map